import { useQuery } from '@apollo/client'
import classnames from 'classnames'
import { capitalize } from 'lodash'
import { useRouter } from 'next/router'

import { stringParam } from 'lib/query-helper'
import FetchingPanel from 'components/FetchingPanel'
import AdjustStockOnHandModal from '../AdjustStockOnHandModal'

import GET_VARIETIES_FOR_PRODUCT from './graphql/GetVarietiesForProduct.graphql'
import { GetVarietiesForProduct, GetVarietiesForProductVariables } from './graphql/__generated__/GetVarietiesForProduct'
import { StockOnHandModeEnum } from '../../../../../../__generated__/globalTypes'

import styles from './VarietiesRow.module.css'

type VarietiesRowProps = {
  className?: string
  productId: string
  outOfStockThreshold: number | null | undefined
}

const VarietiesRow = ({ className, productId, outOfStockThreshold }: VarietiesRowProps) => {
  const router = useRouter()
  const stockOnHandMode = (stringParam(router.query.stockOnHandMode) ?? undefined) as StockOnHandModeEnum.OUT_OF_STOCK

  const { loading, data } = useQuery<GetVarietiesForProduct, GetVarietiesForProductVariables>(
    GET_VARIETIES_FOR_PRODUCT,
    {
      variables: { productId },
      fetchPolicy: stockOnHandMode ? 'cache-and-network' : 'cache-first'
    }
  )
  const products = data?.currentSeller.draftProduct.varieties.nodes ?? []

  if (loading) {
    return (
      <tr className={className}>
        <td colSpan={9}>
          <FetchingPanel />
        </td>
      </tr>
    )
  }

  return (
    <>
      {products.map((variety, key) => {
        const id = variety.id
        const name = variety.name
        const sku = variety.sku
        const minimumOrderQuantity = variety.minimumOrderQuantity
        const stockOnHand = variety.stockOnHand
        const price = variety.price?.toFixed(2)
        const unit = capitalize(variety.unit ?? '')

        return (
          <tr key={key} className={classnames(className, styles.row)}>
            <td colSpan={2} />
            <td className={styles.border}>
              <p className={styles.itemName}>{name}</p>
              <p className={styles.itemSku}>{sku}</p>
            </td>
            <td className={styles.border} colSpan={2} />
            <td className={classnames(styles.border, styles.center)}>{minimumOrderQuantity}</td>
            <td className={classnames(styles.border, styles.center)}>
              <div className={styles.stockOnHandContainer}>
                <p
                  className={classnames(styles.stockOnHand, {
                    [styles.stockBelowThreshold]: (stockOnHand ?? 0) <= (outOfStockThreshold ?? 0)
                  })}>
                  {stockOnHand}
                </p>
                <AdjustStockOnHandModal varietyId={id} />
              </div>
            </td>
            <td className={classnames(styles.border, styles.right, styles.priceContainer)}>
              <p className={styles.itemPrice}>${price}</p>
              <p className={styles.itemUnit}>{unit}</p>
            </td>
            <td />
          </tr>
        )
      })}
    </>
  )
}

export default VarietiesRow
